import React, { useRef, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import {
  PenTool,
  RotateCcw,
  Check,
  User
} from 'lucide-react';

const SignaturePad = ({ 
  onSave, 
  onClear,
  recipientName,
  onRecipientNameChange,
  className = '' 
}) => {
  const sigCanvas = useRef(null);
  const [isEmpty, setIsEmpty] = useState(true);

  const handleClear = () => {
    sigCanvas.current?.clear();
    setIsEmpty(true);
    onClear?.();
  };

  const handleEnd = () => {
    setIsEmpty(sigCanvas.current?.isEmpty() ?? true);
  };

  const handleSave = () => {
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      const dataUrl = sigCanvas.current.toDataURL('image/png');
      onSave?.(dataUrl, recipientName);
    }
  };

  return (
    <Card className={`border-2 border-gray-200 ${className}`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <PenTool className="h-4 w-4 text-green-500" />
          Signature
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Recipient Name Input */}
        <div className="space-y-2">
          <Label htmlFor="recipient-name" className="flex items-center gap-2 text-sm">
            <User className="h-4 w-4" />
            Recipient Name
          </Label>
          <Input
            id="recipient-name"
            placeholder="Enter recipient's name"
            value={recipientName}
            onChange={(e) => onRecipientNameChange?.(e.target.value)}
            className="text-base"
            data-testid="recipient-name-input"
          />
        </div>

        {/* Signature Canvas */}
        <div className="relative">
          <div className="border-2 border-dashed border-gray-300 rounded-lg bg-gray-50 overflow-hidden">
            <SignatureCanvas
              ref={sigCanvas}
              canvasProps={{
                className: 'w-full h-40 touch-none',
                style: { width: '100%', height: '160px' }
              }}
              backgroundColor="rgb(249, 250, 251)"
              penColor="black"
              onEnd={handleEnd}
              data-testid="signature-canvas"
            />
          </div>
          {isEmpty && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <p className="text-gray-400 text-sm">Sign here</p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={handleClear}
            className="flex-1 gap-2"
            data-testid="clear-signature-btn"
          >
            <RotateCcw className="h-4 w-4" />
            Clear
          </Button>
          <Button
            onClick={handleSave}
            disabled={isEmpty || !recipientName?.trim()}
            className="flex-1 gap-2 bg-green-500 hover:bg-green-600"
            data-testid="save-signature-btn"
          >
            <Check className="h-4 w-4" />
            Accept
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default SignaturePad;
